import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'

/**
 * White signal v2 building blocks for the Devices, Locations, Clicks, Forms
 * and Bots tabs. Values are ported 1:1 from Paper → App →
 * "App v3 · Analytics Audience · LP-aligned" (same anatomy as the v3
 * Analytics Overview artboard): a verdict line, one `.well` per page, raised
 * white cards, divided KPI tiles, and gray bar rows. Orange marks one answer
 * per view (`AnswerChip`, the focus series); everything else is ink or muted.
 */

export function Verdict({ title, meta }: { title: ReactNode; meta?: ReactNode }) {
  return (
    <div className="flex flex-col gap-1 py-1">
      <h2 className="text-[22px] font-semibold leading-7 tracking-[-0.02em] text-foreground">{title}</h2>
      {meta && <p className="text-[13px] leading-4 text-muted-foreground">{meta}</p>}
    </div>
  )
}

export function RaisedCard({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('surface-raised flex flex-col overflow-clip', className)}>{children}</div>
}

export function CardHead({ title, children }: { title: ReactNode; children?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-2.5 px-[18px] py-3.5">
      <h3 className="flex-1 text-sm font-semibold leading-[18px] text-foreground">{title}</h3>
      {children}
    </div>
  )
}

export interface KpiTileProps {
  label: ReactNode
  value: ReactNode
  /** Secondary context next to the value, e.g. a count. Never colored. */
  sub?: ReactNode
}

export function KpiStrip({ tiles }: { tiles: KpiTileProps[] }) {
  return (
    <RaisedCard className="flex-row overflow-x-auto scrollbar-hide">
      {tiles.map((tile, i) => (
        <div
          key={i}
          className={cn(
            'flex min-w-[140px] flex-1 flex-col gap-1.5 px-[18px] py-4',
            i > 0 && 'border-l border-border/60',
          )}
        >
          <span className="flex items-center gap-1 text-xs text-muted-foreground">{tile.label}</span>
          <span className="flex items-baseline gap-1.5">
            <span className="truncate text-[22px] font-semibold leading-7 tracking-[-0.02em] text-foreground tabular-nums">
              {tile.value}
            </span>
            {tile.sub != null && (
              <span className="text-xs font-medium text-muted-foreground tabular-nums">{tile.sub}</span>
            )}
          </span>
        </div>
      ))}
    </RaisedCard>
  )
}

/** The one orange value on a view: primary-strong text on the primary tint. */
export function AnswerChip({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex rounded-[7px] bg-primary-tint px-[7px] py-0.5 font-semibold text-primary-strong ring-1 ring-primary-tint-ring tabular-nums',
        className,
      )}
    >
      {children}
    </span>
  )
}

export interface SegmentOption<T extends string> {
  value: T
  label: ReactNode
}

/** White-on-muted segmented control (never orange). */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
}: {
  options: SegmentOption<T>[]
  value: T
  onChange: (value: T) => void
}) {
  return (
    <div role="tablist" className="inline-flex max-w-full overflow-x-auto rounded-[7px] bg-muted p-0.5 scrollbar-hide">
      {options.map((opt) => {
        const active = opt.value === value
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(opt.value)}
            className={cn(
              'flex h-[22px] shrink-0 items-center gap-1.5 whitespace-nowrap rounded-[5px] px-2 text-xs transition-colors',
              active
                ? 'bg-background font-medium text-foreground shadow-[0_1px_2px_rgb(0_0_0/0.08)]'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}

/** Multi-select filter chip: ink when active, hairline when not. */
export function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'inline-flex h-6 items-center gap-1 rounded-[7px] px-2 text-xs transition-colors',
        active
          ? 'bg-foreground font-medium text-background'
          : 'text-muted-foreground ring-1 ring-border hover:text-foreground',
      )}
    >
      {children}
    </button>
  )
}

/** Whole percent at 10% and above, one decimal below it. */
export function formatShare(share: number): string {
  return `${share.toFixed(share > 0 && share < 10 ? 1 : 0)}%`
}

export interface BarRow {
  key: string
  label: ReactNode
  value: ReactNode
  share: number
  leading?: ReactNode
}

/** Column header over a bar-row list. */
export function BarRowsHeader({
  label,
  valueLabel,
  valueWidth = 'w-[110px]',
}: {
  label: ReactNode
  valueLabel: ReactNode
  valueWidth?: string
}) {
  return (
    <div className="flex px-[18px] pb-1.5 text-xs text-muted-foreground">
      <span className="flex-1">{label}</span>
      <span className={cn('shrink-0 text-right', valueWidth)}>{valueLabel}</span>
      <span className="w-14 shrink-0 text-right">Share</span>
    </div>
  )
}

/** Tinted bar rows: the fill width is the row's share (Overview's Pages card). */
export function BarRows({ rows, valueWidth = 'w-[110px]' }: { rows: BarRow[]; valueWidth?: string }) {
  return (
    <div className="flex flex-col">
      {rows.map((row) => (
        <div key={row.key} className="relative mx-2.5 flex h-9 shrink-0 items-center px-2">
          <div
            className="absolute left-0 top-[3px] h-[30px] rounded-md bg-primary-tint"
            style={{ width: `${Math.max(0, Math.min(row.share, 100))}%` }}
            aria-hidden
          />
          <span className="relative flex min-w-0 flex-1 items-center gap-2 text-[13px] leading-4 text-foreground">
            {row.leading}
            <span className="truncate">{row.label}</span>
          </span>
          <span
            className={cn(
              'relative shrink-0 text-right text-[13px] font-medium leading-4 text-foreground tabular-nums',
              valueWidth,
            )}
          >
            {row.value}
          </span>
          <span className="relative w-14 shrink-0 text-right text-xs leading-4 text-muted-foreground tabular-nums">
            {formatShare(row.share)}
          </span>
        </div>
      ))}
    </div>
  )
}

export function CardFooterAction({ children, onClick }: { children: ReactNode; onClick?: () => void }) {
  return (
    <div className="mt-1.5 flex justify-center border-t border-border/60 pb-3 pt-2.5">
      <button
        type="button"
        onClick={onClick}
        className="text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
      >
        {children}
      </button>
    </div>
  )
}

export function CardEmpty({ children }: { children: ReactNode }) {
  return <div className="px-[18px] py-10 text-center text-[13px] text-muted-foreground">{children}</div>
}

export function Pagination({
  from,
  to,
  total,
  onPrev,
  onNext,
  prevDisabled,
  nextDisabled,
  extra,
}: {
  from: number
  to: number
  total: number
  onPrev: () => void
  onNext: () => void
  prevDisabled: boolean
  nextDisabled: boolean
  extra?: ReactNode
}) {
  return (
    <div className="mt-1.5 flex items-center justify-between gap-3 border-t border-border/60 px-[18px] py-2.5">
      <p className="text-xs text-muted-foreground tabular-nums">
        {from} to {to} of {total}
        {extra}
      </p>
      <div className="flex items-center gap-1.5">
        <Button variant="outline" size="sm" className="h-7 px-2.5 text-xs" onClick={onPrev} disabled={prevDisabled}>
          Previous
        </Button>
        <Button variant="outline" size="sm" className="h-7 px-2.5 text-xs" onClick={onNext} disabled={nextDisabled}>
          Next
        </Button>
      </div>
    </div>
  )
}

export interface SplitSegment {
  key: string
  label: ReactNode
  share: number
  count: ReactNode
  /**
   * Bar width (%) when it must differ from `share`: shares taken against a
   * deduped total can add up past 100% for overlapping rows, so the caller
   * scales widths (see `stackedBarWidths`) while the label keeps the true share.
   */
  width?: number
  /** Override the ramp color, for status splits (e.g. human teal / bot amber on Bots). */
  color?: string
}

/**
 * Categorical split: segments walk the chart ramp from chart-1 unless a
 * segment sets its own color. The top share renders as the view's AnswerChip.
 */
export function SplitBar({ segments, answer = true }: { segments: SplitSegment[]; answer?: boolean }) {
  const colors = ['var(--chart-1)', 'var(--chart-2)', 'var(--chart-3)', 'var(--chart-4)', 'var(--chart-5)']
  const visible = segments.filter((s) => s.share > 0)
  return (
    <>
      <div className="flex h-2.5 gap-0.5 px-[18px]">
        {visible.map((seg, i) => (
          <div
            key={seg.key}
            className={cn('h-2.5', i === 0 && 'rounded-l-[5px]', i === visible.length - 1 && 'rounded-r-[5px]')}
            style={{ width: `${seg.width ?? seg.share}%`, backgroundColor: seg.color ?? colors[Math.min(i, colors.length - 1)] }}
          />
        ))}
      </div>
      <div className="flex flex-wrap px-[18px] pb-4 pt-3.5">
        {segments.map((seg, i) => (
          <div
            key={seg.key}
            className={cn('flex min-w-[120px] flex-1 flex-col gap-1.5 py-1', i > 0 && 'border-l border-border/60 pl-[18px]')}
          >
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span
                className="size-2 rounded-[2px]"
                style={{ backgroundColor: seg.color ?? colors[Math.min(i, colors.length - 1)] }}
                aria-hidden
              />
              {seg.label}
            </span>
            <span className="flex items-center gap-2">
              {i === 0 && answer ? (
                <AnswerChip className="text-base leading-6">{formatShare(seg.share)}</AnswerChip>
              ) : (
                <span className="py-0.5 text-base font-semibold leading-6 text-foreground tabular-nums">
                  {formatShare(seg.share)}
                </span>
              )}
              <span className="text-xs text-muted-foreground tabular-nums">{seg.count}</span>
            </span>
          </div>
        ))}
      </div>
    </>
  )
}
